// Price editor on Admin ▸ Products ▸ product (Jake 2026-11-02: "we want to be
// able to change the pricing for each quantity/capacity if necessary — it
// looks like USKY is pulling in wrong right now"). Admin only; the API
// (routes/catalog-admin.ts → services/price-editor.ts) is the gate and checks
// every number again.
//
//   · Quantity rows (every product): From / To / Our cost / List price.
//   · Our USB drives (Venice / New Jersey): two grids, capacity × quantity —
//     "Customer price" (what the website charges) and "Our cost" (Usky's price;
//     we add $0.25 Usky shipping) — with the margin under every customer price.
//
// Saving marks the product as priced by hand: supplier updates leave it alone
// until "Go back to supplier pricing". Quotes already made keep their prices.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ManualPricing {
  byUserId: string | null;
  byName: string | null;
  at: string;
}

interface BracketView {
  quantityFrom: number;
  quantityTo: number | null;
  netCost: string | null;
  listPrice: string | null;
}

interface UsbLadderView {
  style: string;
  capacities: string[];
  sellBands: number[];
  costTiers: number[];
  minimum: number;
  shippingPerPiece: number;
  floors: Array<{ from: number; marginPct: number | null }>;
  sell: Record<string, string[]>;
  uskyCost: Record<string, number[]>;
}

interface ProductPricingView {
  supplierProductId: string;
  productName: string;
  vendorName: string;
  isPreferred: boolean;
  kind: "brackets" | "usb-ladder" | "unavailable";
  problem: string | null;
  manual: ManualPricing | null;
  brackets: BracketView[];
  partCostRows: number;
  usb: UsbLadderView | null;
}

interface SaveFailure {
  message: string;
  errors: string[];
}

function failureOf(err: unknown): SaveFailure {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; errors?: string[] } | null;
    return { message: body?.error ?? `Saving failed (${err.status}).`, errors: Array.isArray(body?.errors) ? body!.errors : [] };
  }
  return { message: "Saving failed. Check your connection and try again.", errors: [] };
}

const qty = (n: number): string => n.toLocaleString("en-US");
const bandLabel = (bands: readonly number[], i: number): string =>
  bands[i + 1] === undefined ? `${qty(bands[i]!)}+` : `${qty(bands[i]!)}–${qty(bands[i + 1]! - 1)}`;
const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function PriceEditorSection(props: { merchdProductId: string; onSaved: () => void }) {
  const key = ["admin", "catalog", "product", props.merchdProductId, "pricing"] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ offers: ProductPricingView[] }>(`/admin/catalog/products/${props.merchdProductId}/pricing`),
  });
  const queryClient = useQueryClient();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: key });
    props.onSaved();
  };

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-ink">Prices</h2>
        <p className="mt-1 text-sm text-muted">
          Change what we pay and what we charge, by quantity. Quotes and orders already made keep their prices; only new
          pricing uses these numbers.
        </p>
      </div>
      {q.isLoading && <p className="text-sm text-muted">Loading prices…</p>}
      {q.isError && <p className="text-sm text-risk">Couldn't load the prices. Refresh the page to try again.</p>}
      {q.data && q.data.offers.length === 0 && <p className="text-sm text-muted">This product has no active supplier product to price.</p>}
      {q.data?.offers.map((o) => (
        <OfferPrices key={o.supplierProductId} offer={o} showHeading={q.data!.offers.length > 1} onSaved={refresh} />
      ))}
    </section>
  );
}

function OfferPrices(props: { offer: ProductPricingView; showHeading: boolean; onSaved: () => void }) {
  const { offer } = props;
  return (
    <div className="space-y-4 rounded-lg border border-rule bg-white px-5 py-5">
      {props.showHeading && (
        <p className="text-base font-semibold text-ink">
          {offer.vendorName} — {offer.productName}
          {offer.isPreferred && <span className="ml-2 text-sm font-normal text-muted">(the one we buy from)</span>}
        </p>
      )}
      <ManualBanner offer={offer} onSaved={props.onSaved} />
      {offer.kind === "unavailable" && <p className="text-sm text-warn">{offer.problem}</p>}
      {offer.kind === "brackets" && <BracketEditor offer={offer} onSaved={props.onSaved} />}
      {offer.kind === "usb-ladder" && offer.usb && <UsbLadderEditor offer={offer} usb={offer.usb} onSaved={props.onSaved} />}
    </div>
  );
}

// ─── Manually priced banner + "Go back to supplier pricing" ──────────────────

function ManualBanner(props: { offer: ProductPricingView; onSaved: () => void }) {
  const { offer } = props;
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clear = useMutation({
    mutationFn: () => api<{ cleared: boolean }>(`/admin/catalog/pricing/${offer.supplierProductId}/manual`, { method: "DELETE" }),
    onError: (err) => setError(failureOf(err).message),
    onSuccess: () => {
      setError(null);
      setConfirming(false);
      props.onSaved();
    },
  });
  if (!offer.manual) {
    return <p className="text-sm text-muted">Prices come from the supplier. Saving here marks them as set by hand, so supplier updates leave them alone.</p>;
  }
  return (
    <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warn/30 bg-warn-bg px-4 py-3 text-[15px] text-ink">
      <span>
        <strong className="font-semibold">Manually priced</strong>
        {offer.manual.byName ? ` by ${offer.manual.byName}` : ""} on {fmtDate(offer.manual.at)}. Supplier price updates leave these
        prices alone.
      </span>
      {confirming ? (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="text-sm">The supplier's prices come back at the next import; until then these stay.</span>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={clear.isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => clear.mutate()} disabled={clear.isPending}>
            {clear.isPending ? "Working…" : "Yes, go back"}
          </Button>
        </span>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
          Go back to supplier pricing
        </Button>
      )}
      {error && <p className="w-full text-sm text-risk">{error}</p>}
    </div>
  );
}

function Problems(props: { failure: SaveFailure | null }) {
  if (!props.failure) return null;
  return (
    <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg px-4 py-3 text-sm text-risk">
      <p className="font-semibold">{props.failure.message}</p>
      {props.failure.errors.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {props.failure.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── A. Quantity rows ────────────────────────────────────────────────────────

interface DraftRow {
  key: number;
  quantityFrom: string;
  quantityTo: string;
  netCost: string;
  listPrice: string;
}

let rowKey = 0;
const toDraft = (b: BracketView[]): DraftRow[] =>
  b.map((r) => ({
    key: ++rowKey,
    quantityFrom: String(r.quantityFrom),
    quantityTo: r.quantityTo === null ? "" : String(r.quantityTo),
    netCost: r.netCost === null ? "" : String(Number(r.netCost)),
    listPrice: r.listPrice === null ? "" : String(Number(r.listPrice)),
  }));

function BracketEditor(props: { offer: ProductPricingView; onSaved: () => void }) {
  const { offer } = props;
  const [rows, setRows] = useState<DraftRow[]>(() => toDraft(offer.brackets));
  const [failure, setFailure] = useState<SaveFailure | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const original = useMemo(() => JSON.stringify(toDraft(offer.brackets).map(({ key: _k, ...r }) => r)), [offer.brackets]);
  useEffect(() => setRows(toDraft(offer.brackets)), [offer.brackets]);
  const dirty = JSON.stringify(rows.map(({ key: _k, ...r }) => r)) !== original;

  const save = useMutation({
    mutationFn: () =>
      api<{ saved: boolean }>(`/admin/catalog/pricing/${offer.supplierProductId}/brackets`, {
        method: "PUT",
        body: JSON.stringify({ rows: rows.map(({ key: _k, ...r }) => r) }),
      }),
    onError: (err) => {
      setSavedNote(null);
      setFailure(failureOf(err));
    },
    onSuccess: () => {
      setFailure(null);
      setSavedNote("Saved. New quotes use these prices.");
      props.onSaved();
    },
  });

  const set = (i: number, field: keyof Omit<DraftRow, "key">, value: string): void =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [field]: value } : r)));
  const addRow = (): void =>
    setRows((rs) => {
      const last = rs[rs.length - 1];
      const nextFrom = last && /^\d+$/.test(last.quantityTo.trim()) ? String(Number(last.quantityTo) + 1) : "";
      return [...rs, { key: ++rowKey, quantityFrom: nextFrom, quantityTo: "", netCost: "", listPrice: "" }];
    });

  return (
    <div className="space-y-3">
      {offer.partCostRows > 0 && (
        <p className="text-sm text-muted">
          This product also has {offer.partCostRows.toLocaleString()} supplier costs by colour/size. On a quote, a colour/size
          with its own supplier cost uses that; these rows are the cost for everything else.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-[15px]">
          <thead>
            <tr className="border-b border-rule text-left text-sm font-semibold text-muted">
              <th className="py-2 pr-3">Quantity from</th>
              <th className="py-2 pr-3">Quantity to</th>
              <th className="py-2 pr-3">Our cost (each)</th>
              <th className="py-2 pr-3">List price (each, optional)</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className="border-b border-brown-50 last:border-0">
                <td className="py-2 pr-3">
                  <Input aria-label={`Row ${i + 1} quantity from`} inputMode="numeric" value={r.quantityFrom} onChange={(e) => set(i, "quantityFrom", e.target.value)} className="w-28" />
                </td>
                <td className="py-2 pr-3">
                  <Input
                    aria-label={`Row ${i + 1} quantity to`}
                    inputMode="numeric"
                    value={r.quantityTo}
                    placeholder={i === rows.length - 1 ? "and up" : ""}
                    onChange={(e) => set(i, "quantityTo", e.target.value)}
                    className="w-28"
                  />
                </td>
                <td className="py-2 pr-3">
                  <MoneyInput label={`Row ${i + 1} our cost`} value={r.netCost} onChange={(v) => set(i, "netCost", v)} />
                </td>
                <td className="py-2 pr-3">
                  <MoneyInput label={`Row ${i + 1} list price`} value={r.listPrice} onChange={(v) => set(i, "listPrice", v)} />
                </td>
                <td className="py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} disabled={rows.length <= 1}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-muted">Leave the last row's "Quantity to" empty to mean "and up". Prices in US dollars.</p>
      <Problems failure={failure} />
      {savedNote && !dirty && <p className="text-sm text-ok">{savedNote}</p>}
      <div className="flex flex-wrap justify-between gap-2">
        <Button size="sm" variant="outline" onClick={addRow}>
          Add row
        </Button>
        <span className="flex gap-2">
          <Button size="sm" variant="ghost" disabled={!dirty || save.isPending} onClick={() => { setRows(toDraft(offer.brackets)); setFailure(null); }}>
            Undo changes
          </Button>
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save prices"}
          </Button>
        </span>
      </div>
    </div>
  );
}

function MoneyInput(props: { label: string; value: string; onChange: (v: string) => void; className?: string; invalid?: boolean }) {
  return (
    <span className="relative inline-flex items-center">
      <span className="pointer-events-none absolute left-3 text-sm text-muted">$</span>
      <Input
        aria-label={props.label}
        inputMode="decimal"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className={cn("w-28 pl-6", props.invalid && "border-risk", props.className)}
      />
    </span>
  );
}

// ─── B. Our USB drives: capacity × quantity ──────────────────────────────────

type Grid = Record<string, string[]>;
const sellDraft = (u: UsbLadderView): Grid => Object.fromEntries(u.capacities.map((c) => [c, [...(u.sell[c] ?? [])]]));
const costDraft = (u: UsbLadderView): Grid => Object.fromEntries(u.capacities.map((c) => [c, (u.uskyCost[c] ?? []).map((n) => String(n))]));

const MONEY = /^\d+(\.\d{1,3})?$/;
const num = (s: string | undefined): number | null => {
  const t = (s ?? "").trim().replace(/^\$/, "");
  return MONEY.test(t) && Number(t) > 0 ? Number(t) : null;
};

/** Margin on a customer price, against our delivered cost at the band's first
 *  piece (the dearest cost in the band) — the same rule as the server. */
function cellMargin(u: UsbLadderView, cost: Grid, cap: string, bandIndex: number, sellText: string): { pct: number; floor: number | null; tone: "risk" | "warn" | "ok" } | null {
  const sell = num(sellText);
  if (sell === null) return null;
  const from = u.sellBands[bandIndex]!;
  let tier = 0;
  u.costTiers.forEach((t, i) => { if (from >= t) tier = i; });
  const usky = num(cost[cap]?.[tier]);
  if (usky === null) return null;
  const delivered = Math.round((usky + u.shippingPerPiece) * 1000) / 1000;
  const margin = 1 - delivered / sell;
  const floor = u.floors[bandIndex]?.marginPct ?? null;
  const tone = margin < 0 ? "risk" : floor !== null && margin * 100 < floor - 1e-9 ? "warn" : "ok";
  return { pct: Math.round(margin * 1000) / 10, floor, tone };
}

function UsbLadderEditor(props: { offer: ProductPricingView; usb: UsbLadderView; onSaved: () => void }) {
  const { offer, usb } = props;
  const [sell, setSell] = useState<Grid>(() => sellDraft(usb));
  const [cost, setCost] = useState<Grid>(() => costDraft(usb));
  const [failure, setFailure] = useState<SaveFailure | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  useEffect(() => {
    setSell(sellDraft(usb));
    setCost(costDraft(usb));
  }, [usb]);
  const dirty =
    JSON.stringify(sell) !== JSON.stringify(sellDraft(usb)) || JSON.stringify(cost) !== JSON.stringify(costDraft(usb));

  const tally = useMemo(() => {
    let below = 0;
    let loses = 0;
    for (const cap of usb.capacities) {
      (sell[cap] ?? []).forEach((s, i) => {
        const m = cellMargin(usb, cost, cap, i, s);
        if (m?.tone === "risk") loses += 1;
        else if (m?.tone === "warn") below += 1;
      });
    }
    return { below, loses };
  }, [sell, cost, usb]);

  const save = useMutation({
    mutationFn: () =>
      api<{ saved: boolean; warnings: string[] }>(`/admin/catalog/pricing/${offer.supplierProductId}/usb-ladder`, {
        method: "PUT",
        body: JSON.stringify({ sell, cost }),
      }),
    onError: (err) => {
      setWarnings(null);
      setFailure(failureOf(err));
    },
    onSuccess: (out) => {
      setFailure(null);
      setWarnings(out.warnings);
      props.onSaved();
    },
  });

  const setCell = (which: "sell" | "cost", cap: string, i: number, v: string): void => {
    const upd = (g: Grid): Grid => ({ ...g, [cap]: (g[cap] ?? []).map((x, j) => (j === i ? v : x)) });
    if (which === "sell") setSell(upd);
    else setCost(upd);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-ink">Customer price</h3>
        <p className="text-sm text-muted">
          What the website charges for one drive, logo included, by {usb.capacities.length > 0 ? "capacity" : "option"} and order
          quantity (minimum {qty(usb.minimum)}). Under each price: our margin. Amber = below our usual margin for that quantity;
          red = we lose money. Shipping is charged separately.
        </p>
        <div className="overflow-x-auto">
          <table className="text-[15px]">
            <thead>
              <tr className="border-b border-rule text-left text-sm font-semibold text-muted">
                <th className="py-2 pr-4">Capacity</th>
                {usb.sellBands.map((_, i) => (
                  <th key={i} className="whitespace-nowrap py-2 pr-2">
                    {bandLabel(usb.sellBands, i)}
                    <span className="block text-[13px] font-normal">usual {usb.floors[i]?.marginPct ?? "—"}%</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usb.capacities.map((cap) => (
                <tr key={cap} className="border-b border-brown-50 align-top last:border-0">
                  <td className="py-2 pr-4 font-semibold text-ink">{cap}</td>
                  {(sell[cap] ?? []).map((s, i) => {
                    const m = cellMargin(usb, cost, cap, i, s);
                    return (
                      <td key={i} className="py-2 pr-2">
                        <MoneyInput label={`${cap} customer price at ${bandLabel(usb.sellBands, i)}`} value={s} onChange={(v) => setCell("sell", cap, i, v)} className="w-24" invalid={num(s) === null} />
                        <span className={cn("mt-1 block text-sm", m?.tone === "risk" ? "font-semibold text-risk" : m?.tone === "warn" ? "font-semibold text-warn" : "text-muted")}>
                          {m ? `${m.pct.toFixed(1)}%` : "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold text-ink">Our cost</h3>
        <p className="text-sm text-muted">
          Usky's price per drive (logo and box included) by Usky's quantity tiers. We add ${usb.shippingPerPiece.toFixed(2)} Usky
          shipping to each — the "delivered" figure is what margins and quotes use.
        </p>
        <div className="overflow-x-auto">
          <table className="text-[15px]">
            <thead>
              <tr className="border-b border-rule text-left text-sm font-semibold text-muted">
                <th className="py-2 pr-4">Capacity</th>
                {usb.costTiers.map((_, i) => (
                  <th key={i} className="whitespace-nowrap py-2 pr-2">
                    {bandLabel(usb.costTiers, i)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usb.capacities.map((cap) => (
                <tr key={cap} className="border-b border-brown-50 align-top last:border-0">
                  <td className="py-2 pr-4 font-semibold text-ink">{cap}</td>
                  {(cost[cap] ?? []).map((c, i) => {
                    const n = num(c);
                    return (
                      <td key={i} className="py-2 pr-2">
                        <MoneyInput label={`${cap} Usky price at ${bandLabel(usb.costTiers, i)}`} value={c} onChange={(v) => setCell("cost", cap, i, v)} className="w-24" invalid={n === null} />
                        <span className="mt-1 block text-sm text-muted">
                          {n === null ? "—" : `delivered $${(Math.round((n + usb.shippingPerPiece) * 1000) / 1000).toFixed(2)}`}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(tally.below > 0 || tally.loses > 0) && (
        <p className="text-sm text-warn">
          {tally.loses > 0 && <strong className="font-semibold text-risk">{tally.loses} price{tally.loses === 1 ? "" : "s"} lose money. </strong>}
          {tally.below > 0 && `${tally.below} price${tally.below === 1 ? " is" : "s are"} below our usual margin. `}
          You can still save.
        </p>
      )}
      <Problems failure={failure} />
      {warnings && !dirty && (
        <div className="text-sm text-ok">
          Saved. The website, cart and new quotes use these prices now.
          {warnings.length > 0 && <span className="text-warn"> {warnings.length} price{warnings.length === 1 ? " is" : "s are"} under the usual margin (noted in the history).</span>}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" disabled={!dirty || save.isPending} onClick={() => { setSell(sellDraft(usb)); setCost(costDraft(usb)); setFailure(null); }}>
          Undo changes
        </Button>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save prices"}
        </Button>
      </div>
    </div>
  );
}
